// useMoveTask.js

import { useMutation } from "@apollo/client";
import { MOVE_TASK } from "../../graphql/mutations/ProjectEditContext";

/**
 * Custom hook to move a task using GraphQL mutation.
 *
 * @returns {[Function, Object]} A function to move the task and an object containing data, error, and loading states.
 */
const useMoveTask = () => {
  const [mutation, { data, error, loading }] = useMutation(MOVE_TASK);

  /**
   * Function to move a task with the provided parameters.
   *
   * @param {MoveTaskParams} params - The task move parameters.
   * @returns {MoveTaskResult|undefined} The result of the mutation.
   *
   * 1. If the mutation fails due to network or server issues, `undefined` is returned.
   * 2. If the mutation is executed but rejected by the server (e.g., task move failed),
   *    a `MoveTaskResult` object with `success: false` and an `errorResult` is returned.
   */
  const moveTask = async ({ projectId, taskId, parentTaskId, insertAfterTaskId }) => {
    const variables = {
      moveTaskParams: {
        projectId,
        taskId,
        parentTaskId,
        insertAfterTaskId,
      }
    };

    const res = await mutation({ variables });

    // Return result object or undefined if the mutation fails (e.g., network error).
    return res?.data?.projects?.moveTask;
  };

  return [moveTask, { data, error, loading }];
};

/**
 * @typedef {Object} MoveTaskParams
 * @property {number} projectId - The project ID.
 * @property {number} taskId - The task ID to move.
 * @property {number} [parentTaskId] - The new parent task ID, if applicable.
 * @property {number} [insertAfterTaskId] - The task ID to insert after, if applicable.
 */

/**
 * @typedef {Object} MoveTaskResult
 * @property {boolean} success - Indicates whether the task move was successful.
 * @property {Object|null} errorResult - Present only if the move failed. Contains details of the error.
 */

/**
 * Exporting the custom hook for external usage.
 */
export { useMoveTask };
